import React, { useState } from 'react';
import {
  MapPin, Maximize2, MessageCircle, Phone,
  ArrowRight, Eye, Flame, Sparkles, CheckCircle2, Camera, Heart,
  Plane, Ruler, Warehouse, Car, Route,
} from 'lucide-react';
import { getPhoneLink, openWhatsApp } from '../../constants/contact';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';
import { useFavorites } from '../../hooks/useFavorites';
import { KalyaniStoryModal } from '../KalyaniStoryModal';
const AreaIntelModal = React.lazy(() => import('./AreaIntelModal').then((m) => ({ default: m.AreaIntelModal })));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  Available: { dot: 'bg-emerald-400', label: 'Available', pill: 'bg-emerald-950/60 text-emerald-200 border-emerald-400/30' },
  Sold:      { dot: 'bg-rose-400',    label: 'Sold',      pill: 'bg-rose-950/60 text-rose-200 border-rose-400/30' },
  Reserved:  { dot: 'bg-amber-400',   label: 'Reserved',  pill: 'bg-amber-950/60 text-amber-200 border-amber-400/30' },
};

// Kalyani Intl Airport (KLY, WB — confirmed) reference point. Any plot whose
// location string mentions the surrounding towns OR whose lat/lng sits inside
// a ~30 km catchment gets a green "New Kalyani Airport nearby" chip so buyers
// instantly see the appreciation catalyst.
const KALYANI_LAT = 22.9749;
const KALYANI_LNG = 88.4342;
const KALYANI_KEYWORDS = [
  'kalyani', 'nadia', 'halisahar', 'chakdaha', 'nimta', 'barrackpore',
  'baranagar', 'panihati', 'kanchrapara', 'naihati', 'bt road', 'nh 12', 'nh-12',
];

function isNearKalyani(property) {
  const loc = String(property?.location || '').toLowerCase();
  if (KALYANI_KEYWORDS.some((k) => loc.includes(k))) return true;
  const lat = parseFloat(property?.latitude);
  const lng = parseFloat(property?.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  // Rough Haversine — 30 km radius
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat - KALYANI_LAT);
  const dLng = toRad(lng - KALYANI_LNG);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(KALYANI_LAT)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2;
  const km = 2 * R * Math.asin(Math.sqrt(a));
  return km <= 30;
}

const DEAL_STYLE = {
  hot_deal:    { label: '🔥 HOT DEAL',     cls: 'am-tile-deal am-tile-deal--hot' },
  bumper_deal: { label: '💥 BUMPER DEAL',  cls: 'am-tile-deal am-tile-deal--bumper' },
};

/** Single card in the public property grid. Redesigned as an
 *  edge-to-edge realistic listing tile — full-bleed image with a soft
 *  glass-morphism info footer, an emerald price ribbon, live-activity
 *  chips and three CTAs that share the tile with the map/gallery.
 *  Interaction bubbles up via `onView`. */
export const PropertyCard = ({ property, inquiryStats, onView, index = 0 }) => {
  const [intel, setIntel] = useState(false);
  const imgSrc = property.image?.match(/^\/api\/(uploads|files)\//)
    ? `${BACKEND_URL}${property.image}`
    : property.image;
  const stats = inquiryStats?.[property.id];
  const { ref } = useRevealOnScroll();
  const { isFav, toggle } = useFavorites();
  const favActive = isFav(property.id);
  const [kalyaniOpen, setKalyaniOpen] = useState(false);

  const status = STATUS_STYLE[property.status] || STATUS_STYLE.Available;
  const priceLabel = String(property.price || '').replace('₹', '').trim();
  const imgCount = property.images?.length || (property.image ? 1 : 0);
  const isHot = (stats?.site_visits_total || 0) >= 3 || (stats?.views_today || 0) >= 25;
  const showKalyani = isNearKalyani(property);

  // Alternate slide direction per column (0→left, 1→up, 2→right) so a 3-col
  // grid feels like the cards fan in from both edges. Delay staggers ~80ms
  // per card, capped by row so late rows don't wait too long.
  const dir = ['am-reveal-left', 'am-reveal-up', 'am-reveal-right'][index % 3];
  const staggerMs = (index % 6) * 90;

  const whatsappMessage = () => (
    `Hi! I'm interested in ${property.title} located in ${property.location}. ` +
    `Price: ${property.price}, Area: ${property.area}. Please provide more details.`
  );

  return (
    <article
      ref={ref}
      key={property.id}
      className={`am-property-tile am-reveal am-reveal-card ${dir} group`}
      style={{ transitionDelay: `${staggerMs}ms` }}
      data-testid={`property-card-${property.id}`}
      onClick={(e) => {
        // Whole-tile clickability without swallowing CTA clicks.
        if (e.target.closest('button, a')) return;
        onView(property);
      }}
    >
      {/* --- HERO IMAGE (full-bleed) --- */}
      <div className="am-tile-hero">
        <img
          src={imgSrc}
          alt={property.title}
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200&q=80';
          }}
          className="am-tile-img"
          loading="lazy"
        />
        <div className="am-tile-scrim" aria-hidden="true" />

        {/* Top-left: type chip */}
        <div className="am-tile-topleft">
          <span className="am-tile-chip am-tile-chip--type">
            {property.type}
          </span>
          {property.verified !== false && (
            <span
              className="am-tile-chip am-tile-chip--verified"
              title="Verified by Acre Mitra"
            >
              <CheckCircle2 size={11} /> Verified
            </span>
          )}
        </div>

        {/* Top-right: status pill + image count */}
        <div className="am-tile-topright">
          <span className={`am-tile-status ${status.pill}`}>
            <span className={`am-tile-status-dot ${status.dot}`} />
            {status.label}
          </span>
          {imgCount > 1 && (
            <span className="am-tile-photocount" title="Photos available">
              <Camera size={11} /> {imgCount}
            </span>
          )}
        </div>

        {/* Hot badge (pulsing) */}
        {isHot && (
          <span className="am-tile-hot" title="Hot listing — high recent interest">
            <span className="am-tile-hot-ping" />
            <Flame size={11} /> Hot
          </span>
        )}

        {/* Admin marketing flag ribbon (hot deal / bumper deal) */}
        {property.deal_flag && DEAL_STYLE[property.deal_flag] && (
          <span
            className={DEAL_STYLE[property.deal_flag].cls}
            data-testid={`deal-ribbon-${property.id}`}
          >
            {DEAL_STYLE[property.deal_flag].label}
          </span>
        )}

        {/* Kalyani Airport catalyst chip — clickable → opens "Why Kalyani?" modal */}
        {showKalyani && (
          <button
            type="button"
            className="am-tile-kalyani"
            title="Newly confirmed Kalyani International Airport is nearby — click to learn why"
            data-testid={`kalyani-chip-${property.id}`}
            onClick={(e) => { e.stopPropagation(); setKalyaniOpen(true); }}
          >
            <Plane size={11} /> Kalyani Airport nearby
          </button>
        )}

        {/* Save-for-later heart */}
        <button
          type="button"
          className="am-tile-fav"
          data-active={favActive}
          onClick={(e) => { e.stopPropagation(); toggle(property.id); }}
          aria-pressed={favActive}
          aria-label={favActive ? 'Remove from favourites' : 'Save to favourites'}
          data-testid={`fav-toggle-${property.id}`}
          title={favActive ? 'Saved to favourites' : 'Save for later'}
        >
          <Heart size={16} />
        </button>

        {/* Bottom: title + location + price ribbon overlay */}
        <div className="am-tile-heroFoot">
          <div className="am-tile-heroFoot-text">
            <h3 className="am-tile-title">{property.title}</h3>
            <div className="am-tile-loc">
              <MapPin size={12} className="opacity-90" />
              <span className="truncate">{property.location}</span>
            </div>
          </div>
          <div className="am-tile-price">
            <span className="am-tile-price-currency">₹</span>
            <span className="am-tile-price-value">{priceLabel}</span>
          </div>
        </div>
      </div>

      {/* --- INFO STRIP --- */}
      <div className="am-tile-body">
        <div className="am-tile-metaline">
          <span className="am-tile-meta">
            <Maximize2 size={13} />
            <strong>{property.area}</strong>
          </span>
          {property.property_code && (
            <span
              className="am-tile-code"
              data-testid={`property-code-${property.id}`}
              title="Property code"
            >
              {property.property_code}
            </span>
          )}
        </div>

        {/* Per-unit chips */}
        {(property.price_per_bigha || property.price_per_katha) && (
          <div
            className="am-tile-perunit"
            data-testid={`per-unit-${property.id}`}
          >
            {property.price_per_bigha && (
              <span className="am-tile-perunit-chip">
                ₹{Number(property.price_per_bigha).toLocaleString('en-IN')}
                <em>/bigha</em>
              </span>
            )}
            {property.price_per_katha && (
              <span className="am-tile-perunit-chip">
                ₹{Number(property.price_per_katha).toLocaleString('en-IN')}
                <em>/katha</em>
              </span>
            )}
          </div>
        )}

        {/* Physical + amenities quick-glance strip */}
        {(property.frontage_ft || property.lift_available || property.parking_available
          || (property.amenities && property.amenities.length > 0)) && (
          <div
            className="am-tile-amenities"
            data-testid={`amenities-${property.id}`}
          >
            {property.frontage_ft && (
              <span className="am-tile-amen-chip" title="Road frontage">
                <Ruler size={11} /> {Number(property.frontage_ft)} ft frontage
              </span>
            )}
            {property.lift_available && (
              <span className="am-tile-amen-chip" title="Lift available">
                🛗 Lift
              </span>
            )}
            {property.parking_available && (
              <span className="am-tile-amen-chip" title="Parking available">
                <Car size={11} /> Parking
              </span>
            )}
            {property.amenities && property.amenities.slice(0, 2).map((a) => (
              <span key={a} className="am-tile-amen-chip am-tile-amen-chip--soft" title={a}>
                <Warehouse size={10} /> {a}
              </span>
            ))}
            {property.amenities && property.amenities.length > 2 && (
              <span className="am-tile-amen-chip am-tile-amen-chip--soft">
                +{property.amenities.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Bumper-deal live countdown */}
        {property.deal_flag === 'bumper_deal' && (
          <DealCountdown propertyId={property.id} endsAt={property.deal_ends_at} />
        )}

        {/* Live-activity chips */}
        {stats && (
          <div
            className="am-tile-activity"
            data-testid={`inquiries-${property.id}`}
          >
            <span className="am-tile-activity-chip">
              <Eye size={11} />
              <strong>{stats.views_today}</strong>&nbsp;today
            </span>
            {stats.site_visits_total > 0 && (
              <span className="am-tile-activity-chip am-tile-activity-chip--hot">
                <Flame size={11} />
                <strong>{stats.site_visits_total}</strong>&nbsp;visit
                {stats.site_visits_total > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* CTAs */}
        {Number.isFinite(parseFloat(property.latitude)) && Number.isFinite(parseFloat(property.longitude)) && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setIntel(true); }}
            className="mb-2 w-full h-9 rounded-lg border border-emerald-600/40 bg-emerald-50 text-emerald-800 text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-emerald-100 transition-colors"
            data-testid={`area-intel-${property.id}`}>
            <Route size={13} /> Highways, traffic & area deals
          </button>)}
        {intel && <React.Suspense fallback={null}><AreaIntelModal property={property} onClose={() => setIntel(false)} /></React.Suspense>}
        <div className="am-tile-cta">
          <button
            type="button"
            onClick={() => onView(property)}
            className="am-tile-cta-primary"
            data-testid={`view-details-${property.id}`}
          >
            View Details
            <ArrowRight size={14} />
          </button>
          <a
            href={getPhoneLink()}
            className="am-tile-cta-secondary"
            title="Call"
            aria-label="Call"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone size={14} />
          </a>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openWhatsApp(whatsappMessage());
            }}
            className="am-tile-cta-secondary am-tile-cta-whatsapp"
            title="WhatsApp"
            aria-label="Chat on WhatsApp"
          >
            <MessageCircle size={14} />
          </button>
        </div>
      </div>
      {/* "Why Kalyani?" briefing modal */}
      <KalyaniStoryModal open={kalyaniOpen} onClose={() => setKalyaniOpen(false)} />
    </article>
  );
};

/**
 * Small live-ticking countdown pill for BUMPER DEAL cards.
 * If admin didn't set `deal_ends_at`, we synthesise a stable per-property
 * end-time (~48 h from a hash of the property id) so every card animates and
 * feels alive without a database round-trip.
 */
const DealCountdown = ({ propertyId, endsAt }) => {
  const targetTs = React.useMemo(() => {
    if (endsAt) {
      const t = new Date(endsAt).getTime();
      if (Number.isFinite(t)) return t;
    }
    // Deterministic ~48h window per property so it looks live but doesn't jump
    let h = 0;
    for (let i = 0; i < String(propertyId || '').length; i++) {
      h = (h * 31 + String(propertyId).charCodeAt(i)) & 0xffffffff;
    }
    const bucketHours = 12 + (Math.abs(h) % 48); // 12..60h from now
    return Date.now() + bucketHours * 3600_000;
  }, [propertyId, endsAt]);

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remain = Math.max(0, targetTs - now);
  const days = Math.floor(remain / 86_400_000);
  const hrs  = Math.floor((remain % 86_400_000) / 3_600_000);
  const mins = Math.floor((remain % 3_600_000) / 60_000);
  const secs = Math.floor((remain % 60_000) / 1000);

  if (remain === 0) return null;

  return (
    <div className="am-tile-countdown" data-testid={`deal-countdown-${propertyId}`}>
      <span className="am-tile-countdown-label">💥 Ends in</span>
      <div className="am-tile-countdown-cells">
        {days > 0 && (
          <span className="am-tile-countdown-cell">
            <b>{String(days).padStart(2, '0')}</b><em>d</em>
          </span>
        )}
        <span className="am-tile-countdown-cell">
          <b>{String(hrs).padStart(2, '0')}</b><em>h</em>
        </span>
        <span className="am-tile-countdown-cell">
          <b>{String(mins).padStart(2, '0')}</b><em>m</em>
        </span>
        <span className="am-tile-countdown-cell am-tile-countdown-cell--pulse">
          <b>{String(secs).padStart(2, '0')}</b><em>s</em>
        </span>
      </div>
    </div>
  );
};

export default PropertyCard;

